import { COPY, programLabel } from '../content';
import { apiGet, type Reports as ReportsData } from '../api';
import { useAsync, navigate } from '../hooks';
import { money, percent, shortDate, lastSeen } from '../format';
import { Spinner, LoadError, Card, StatTile, Pill } from '../components/ui';

export function Reports() {
  const { data, loading, error, reload } = useAsync(() => apiGet<ReportsData>('/v1/admin/reports'), []);

  return (
    <div>
      <h1 className="screen-title">{COPY.reports.title}</h1>
      <p className="screen-sub">{COPY.reports.sub}</p>

      {loading ? <Spinner /> : null}
      {error ? <LoadError message={error} onRetry={reload} /> : null}

      {data ? (
        <>
          <div className="stats">
            <StatTile
              n={percent(data.activeShare.fraction)}
              label={`${COPY.reports.activeShare}: ${data.activeShare.activeCount} of ${data.activeShare.totalCount}`}
            />
            <StatTile n={data.dormant.length} label="Dormant members" />
            <StatTile n={data.topNewProducers.length} label="New producers (90 days)" />
            <StatTile n={data.taxTracker.filter((t) => t.over1099Threshold).length} label="Over 1099 threshold" />
          </div>

          <Card title={COPY.reports.topProducers}>
            {data.topNewProducers.length === 0 ? <p className="muted small">{COPY.reports.producersEmpty}</p> : null}
            {data.topNewProducers.map((m) => (
              <button
                key={m.id}
                className="ghost list-row"
                style={{ width: '100%', textAlign: 'left', padding: '10px 0' }}
                onClick={() => navigate(`members/${m.id}`)}
              >
                <div className="grow">
                  <strong>{m.name}</strong>
                  <div className="small muted">
                    {programLabel(m.program)} | joined {shortDate(m.joinDateIso)}
                  </div>
                </div>
                <span className="cond" style={{ fontSize: 22 }}>
                  {m.referralCount}
                </span>
              </button>
            ))}
          </Card>

          <Card title={COPY.reports.demo}>
            {data.demoActivity.length === 0 ? <p className="muted small">No Demo Centers yet.</p> : null}
            {data.demoActivity.map((d) => (
              <button
                key={d.id}
                className="ghost list-row"
                style={{ width: '100%', textAlign: 'left', padding: '10px 0' }}
                onClick={() => navigate(`members/${d.id}`)}
              >
                <div className="grow">
                  <strong>{d.name}</strong>
                  <div className="small muted">
                    {d.fleetSize} boards on loan | {d.referralCount} referrals | {lastSeen(d.lastActivityIso)}
                  </div>
                </div>
                <Pill tone={d.lifecycleStatus === 'active' ? 'green' : 'amber'}>{d.lifecycleStatus}</Pill>
              </button>
            ))}
          </Card>

          <Card title={`${COPY.reports.tax} (${data.year})`}>
            <p className="muted small">Paid year to date vs the {money(data.thresholdCents)} reporting threshold.</p>
            {data.taxTracker.length === 0 ? <p className="muted small">No one has been paid this year yet.</p> : null}
            {data.taxTracker.map((t) => (
              <div className="list-row" key={t.id}>
                <div className="grow">
                  <div className="row" style={{ gap: 8 }}>
                    <strong>{t.name}</strong>
                    {t.over1099Threshold ? <Pill tone="red">Over threshold</Pill> : null}
                    {t.w9Status === 'on_file' ? <Pill tone="green">W-9 on file</Pill> : null}
                  </div>
                </div>
                <span className="money">{money(t.paidYtdCents)}</span>
              </div>
            ))}
          </Card>

          <Card title={COPY.reports.dormant}>
            {data.dormant.length === 0 ? <p className="muted small">{COPY.reports.dormantEmpty}</p> : null}
            {data.dormant.map((m) => (
              <button
                key={m.id}
                className="ghost list-row"
                style={{ width: '100%', textAlign: 'left', padding: '10px 0' }}
                onClick={() => navigate(`members/${m.id}`)}
              >
                <div className="grow">
                  <strong>{m.name}</strong>
                  <div className="small muted">
                    {programLabel(m.program)} | {lastSeen(m.lastActivityIso)}
                  </div>
                </div>
              </button>
            ))}
          </Card>
        </>
      ) : null}
    </div>
  );
}
